import { getRouteApi } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import PlusIcon from '@/assets/icons/add.svg?react'
import Button from '@/components/Button/Button'
import SplittedHeader from '@/components/SplittedHeader/SplittedHeader'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import Filters from '@/components/common/Filters/Filters'
import useSemesters from '@/queries/useSemesters'
import queryClient from '@/queryClient'
import useAuthStore from '@/store/useAuthStore'

import styles from './AvailabilityManagementView.module.scss'
import AvailabilityLegend from '../components/AvailabilityLegned/AvailabilityLegend'
import AvailiabilityTable, {
  type AvailabilityTableRef
} from '../components/AvailabilityTable/AvailabilityTable'
import UserAvailabilityModal from '../components/UserAvailabilityModal/UserAvailabilityModal'
import useAvailabilityManagementColumns from '../hooks/useAvailabilityManagementColumns'
import useAvailabilityMangementFilters from '../hooks/useAvailabilityManagementFilters'

const routeApi = getRouteApi('/_auth/availability-management/')

const AvailabilityManagementView = () => {
  const { t } = useTranslation(['availabilityManagement', 'common'])
  const { data: semesters } = useSemesters()
  const { semester, day } = routeApi.useSearch()
  const { isSuperAdmin } = useAuthStore()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const availiabilityTableRef = useRef<AvailabilityTableRef>(null)

  const handleChangeSemester = () => {
    availiabilityTableRef.current?.clearAllSelection()
  }

  const { filters, handleClearAll, isSomeFilterSelected } =
    useAvailabilityMangementFilters({
      handleChangeSemester
    })

  const selectedSemester = semesters?.getSemester(semester)

  const isArchivedSemester = selectedSemester?.status === 'archived'
  const { columns, ...manageColumns } = useAvailabilityManagementColumns({
    day,
    routeApi
  })

  return (
    <BasicLayout
      moduleName={t('header.administration', { ns: 'common' })}
      header={
        <h1 className={styles.header}>
          <SplittedHeader
            firstPart={t('header.availability-management')}
            secondPart={selectedSemester?.name || ''}
          />
        </h1>
      }
      breadcrumbs={[{ title: t('common:navigation.availability-management') }]}
    >
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        actionsRender={
          isSuperAdmin ? (
            <Button
              onClick={() => setIsModalOpen(true)}
              icon={<PlusIcon />}
              disabled={isArchivedSemester}
            >
              {t('button.set-availability')}
            </Button>
          ) : undefined
        }
        manageColumns={manageColumns}
      />
      <div className={styles.wrapper}>
        <AvailabilityLegend />
        <AvailiabilityTable
          columns={columns}
          ref={availiabilityTableRef}
          isArchivedSemester={isArchivedSemester}
        />
      </div>
      {selectedSemester ? (
        <UserAvailabilityModal
          semester={selectedSemester}
          onOpenChange={setIsModalOpen}
          open={isModalOpen}
          onSuccess={() =>
            queryClient.invalidateQueries({
              queryKey: ['panelUsersAvailabilityList']
            })
          }
        />
      ) : null}
    </BasicLayout>
  )
}

export default AvailabilityManagementView
